
import { User, Role, AttendanceLog, AppConfig, LogType, LeaveRecord, LeaveType, CloudBackup, Notification, NotificationStatus, SalaryRecord } from '../types';
import { db } from './firebaseConfig';
import * as Firestore from 'firebase/firestore';
import type { QueryDocumentSnapshot, DocumentData, Query } from 'firebase/firestore';

const {
  collection, 
  getDocs, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter, 
  doc, 
  deleteDoc, 
  setDoc,
  getDoc,
  updateDoc,
  writeBatch
} = Firestore;

const USERS_COLLECTION = 'users';
const LOGS_COLLECTION = 'logs';
const LEAVES_COLLECTION = 'leaves';
const CONFIG_COLLECTION = 'config';
const BACKUPS_COLLECTION = 'cloud_backups'; 
const NOTIFICATIONS_COLLECTION = 'notifications';
const SALARY_COLLECTION = 'salary_records';
const CONFIG_DOC_ID = 'main_config';

// --- TIME SYNC STATE (Monotonic Clock) ---
let syncedServerTime: number | null = null;
let syncedPerformanceTime: number | null = null;
let syncPromise: Promise<void> | null = null;

// Helper for ID generation
const generateId = () => {
    try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
    } catch (e) { }
    return 'id-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 9);
};

// --- HELPER: Get Turkish Holidays ---
export const getTurkishHolidays = (year: number): string[] => {
    return [
        `${year}-01-01`, // Yılbaşı
        `${year}-04-23`, // Ulusal Egemenlik ve Çocuk Bayramı
        `${year}-05-01`, // Emek ve Dayanışma Günü
        `${year}-05-19`, // Atatürk'ü Anma, Gençlik ve Spor Bayramı
        `${year}-07-15`, // Demokrasi ve Milli Birlik Günü
        `${year}-08-30`, // Zafer Bayramı
        `${year}-10-29`, // Cumhuriyet Bayramı
        // Tahmini Dini Bayramlar (2025 için)
        `${year}-03-30`, // Ramazan Bayramı 1. Gün
        `${year}-03-31`,
        `${year}-04-01`,
        `${year}-06-06`, // Kurban Bayramı 1. Gün
        `${year}-06-07`,
        `${year}-06-08`,
        `${year}-06-09`
    ];
};

// Initial Data
const INITIAL_ADMIN: User = {
  id: 'admin-1',
  username: 'admin',
  fullName: 'Sistem Yöneticisi',
  password: '123',
  role: Role.ADMIN,
  department: 'Yönetim',
  employmentStartDate: '2020-01-01',
  isActive: true,
  shiftConfig: {
      start: '08:00',
      end: '18:00',
      isSaturdayWorkDay: false
  }
};

const INITIAL_KIOSK_USER: User = {
    id: 'kiosk-user-1',
    username: 'qr@technosis.com.tr',
    fullName: 'QR Kiosk Terminali',
    password: 'kiosk', 
    role: Role.KIOSK,
    department: 'Sistem',
    isActive: true
};

const INITIAL_CONFIG: AppConfig = {
  qrSecret: 'TECHNOSIS_MAIN_ENTRANCE',
  qrRefreshSeconds: 15,
  companyName: 'TECHNOSİS TEKNOLOJİ',
  shiftStart: '08:00',
  shiftEnd: '18:00',
  isSaturdayWorkDay: false,
  saturdayStart: '09:00',
  saturdayEnd: '14:00',
  publicHolidays: [],
  departments: ['Yönetim', 'Muhasebe', 'İnsan Kaynakları', 'Üretim', 'Satış'],
  lastAutoBackupDate: ''
};

// --- CORE TIME SYNC LOGIC ---
const performTimeSync = async () => {
    try {
        const response = await fetch(window.location.href, { 
            method: 'HEAD', 
            cache: 'no-store' 
        });
        const dateHeader = response.headers.get('Date');
        if (dateHeader) {
            syncedServerTime = new Date(dateHeader).getTime();
            syncedPerformanceTime = performance.now();
            console.log("Time Synced via Server Header:", new Date(syncedServerTime));
            return;
        }
    } catch (e) { /* ignore */ }

    // Fallback
    syncedServerTime = Date.now();
    syncedPerformanceTime = performance.now();
};

export const syncTimeWithServer = () => {
    if (!syncPromise) {
        syncPromise = performTimeSync();
    }
    return syncPromise;
};

// EXPORTED: Get Secure Time
export const fetchRealTime = async (): Promise<number> => {
    if (syncedServerTime === null || syncedPerformanceTime === null) {
        await syncTimeWithServer();
    }
    
    if (syncedServerTime !== null && syncedPerformanceTime !== null) {
        return syncedServerTime + (performance.now() - syncedPerformanceTime);
    }
    
    return Date.now();
};

export const initializeDB = async () => {
    syncTimeWithServer();

    const MAX_RETRIES = 3;
    for (let i = 0; i < MAX_RETRIES; i++) {
        try {
            const adminRef = doc(db, USERS_COLLECTION, INITIAL_ADMIN.id);
            const adminDoc = await getDoc(adminRef);
            
            if (!adminDoc.exists()) {
                 const q = query(collection(db, USERS_COLLECTION), where("username", "==", "admin"));
                 const snap = await getDocs(q);
                 if (snap.empty) {
                    await setDoc(adminRef, { ...INITIAL_ADMIN });
                 }
            }

            const kioskQ = query(collection(db, USERS_COLLECTION), where("username", "==", INITIAL_KIOSK_USER.username));
            const kioskSnap = await getDocs(kioskQ);
            if (kioskSnap.empty) {
                await setDoc(doc(db, USERS_COLLECTION, INITIAL_KIOSK_USER.id), INITIAL_KIOSK_USER);
            }

            return { success: true };
        } catch (error: any) {
             const msg = error?.message || '';
            const isPermissionError = msg.includes('permission-denied') || msg.includes('Missing or insufficient permissions');
            
            if (isPermissionError && i < MAX_RETRIES - 1) {
                await new Promise(resolve => setTimeout(resolve, 2000));
                continue;
            }
            if (i === MAX_RETRIES - 1) return { success: false, error };
        }
    }
    return { success: false, error: new Error("Initialization failed") };
};

export const loginUser = async (username: string, password: string): Promise<User | null> => {
    try {
        const q = query(collection(db, USERS_COLLECTION), where("username", "==", username), where("password", "==", password));
        const snap = await getDocs(q);
        if (snap.empty) return null;
        const d = snap.docs[0];
        return { ...d.data(), id: d.id } as User;
    } catch (error) {
        console.error("Login error:", error);
        return null;
    }
};

// --- USER FUNCTIONS ---
export const getUsers = async (): Promise<User[]> => {
  const q = query(collection(db, USERS_COLLECTION));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as User));
};

export const saveUser = async (user: User): Promise<void> => {
  const id = user.id || generateId();
  const newRef = doc(collection(db, USERS_COLLECTION), id);
  await setDoc(newRef, { ...user, id: id });
};

export const updateUser = async (user: User): Promise<void> => {
    const userRef = doc(db, USERS_COLLECTION, user.id);
    await updateDoc(userRef, { ...user });
};

export const deleteUser = async (id: string): Promise<void> => {
    await deleteDoc(doc(db, USERS_COLLECTION, id));
};

export const updateKioskCredentials = async (password: string) => {
    const q = query(collection(db, USERS_COLLECTION), where("role", "==", Role.KIOSK));
    const snap = await getDocs(q);
    if (!snap.empty) {
        const kioskUser = snap.docs[0];
        await updateDoc(kioskUser.ref, { password });
    }
};

export const updateAdminCredentials = async (id: string, username: string, password: string) => {
    const userRef = doc(db, USERS_COLLECTION, id);
    await updateDoc(userRef, { username, password });
};

// --- LOG FUNCTIONS (SCALABLE VERSION) ---

// Gets recent logs (default 1000) for fast initial load
export const getLogs = async (): Promise<AttendanceLog[]> => {
  const q = query(collection(db, LOGS_COLLECTION), orderBy("timestamp", "desc"), limit(1000));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as AttendanceLog));
};

// Gets logs for a specific period (Efficient for Reports/Salary)
// Eliminates the need to load ALL history.
export const getLogsByDateRange = async (startDate: number, endDate: number): Promise<AttendanceLog[]> => {
    const q = query(collection(db, LOGS_COLLECTION), 
        where("timestamp", ">=", startDate),
        where("timestamp", "<=", endDate),
        orderBy("timestamp", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ ...d.data(), id: d.id } as AttendanceLog));
};

export const getLogsByUser = async (userId: string): Promise<AttendanceLog[]> => {
    // Note: To avoid creating a composite index (UserId + Timestamp) manually in Firestore Console,
    // we fetch logs by UserId and then sort/limit in memory.
    // If dataset becomes massive, you should create the index and restore orderBy/limit in query.
    const q = query(collection(db, LOGS_COLLECTION), where("userId", "==", userId));
    const snap = await getDocs(q);
    const logs = snap.docs.map(d => ({ ...d.data(), id: d.id } as AttendanceLog));
    
    // Sort descending by timestamp and limit to 500
    return logs.sort((a, b) => b.timestamp - a.timestamp).slice(0, 500);
};

export const addLog = async (userId: string, userFullName: string, type: LogType, manualTimestamp?: number, ip?: string, userAgent?: string): Promise<AttendanceLog> => {
  const id = generateId();
  let timestamp = manualTimestamp;
  if (!timestamp) timestamp = await fetchRealTime();
  const newLog: AttendanceLog = { id, userId, userFullName, timestamp, type, ip: ip || 'Bilinmiyor', userAgent: userAgent || 'Bilinmiyor' };
  await setDoc(doc(db, LOGS_COLLECTION, id), newLog);
  return newLog;
};

export const updateLog = async (log: AttendanceLog): Promise<void> => {
    await updateDoc(doc(db, LOGS_COLLECTION, log.id), { ...log });
};

export const deleteLog = async (logId: string): Promise<void> => {
    await deleteDoc(doc(db, LOGS_COLLECTION, logId));
};

export const getLastLogForUser = async (userId: string): Promise<AttendanceLog | undefined> => {
  // Same client-side sort fallback as getLogsByUser to avoid index error
  const q = query(collection(db, LOGS_COLLECTION), where("userId", "==", userId));
  const snap = await getDocs(q);
  if(snap.empty) return undefined;
  
  const logs = snap.docs.map(d => ({ ...d.data(), id: d.id } as AttendanceLog));
  logs.sort((a, b) => b.timestamp - a.timestamp);
  return logs[0];
};

// --- LEAVE FUNCTIONS ---
export const getLeaves = async (): Promise<LeaveRecord[]> => {
    const q = query(collection(db, LEAVES_COLLECTION));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ ...d.data(), id: d.id } as LeaveRecord));
};

export const getLeavesByUser = async (userId: string): Promise<LeaveRecord[]> => {
    const q = query(collection(db, LEAVES_COLLECTION), where("userId", "==", userId));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ ...d.data(), id: d.id } as LeaveRecord));
};

export const addLeave = async (leave: Omit<LeaveRecord, 'id' | 'createdAt'>): Promise<LeaveRecord> => {
    const id = generateId();
    const newLeave: LeaveRecord = { ...leave, id, createdAt: Date.now() };
    await setDoc(doc(db, LEAVES_COLLECTION, id), newLeave);
    return newLeave;
};

export const deleteLeave = async (leaveId: string): Promise<void> => {
    await deleteDoc(doc(db, LEAVES_COLLECTION, leaveId));
};

// --- NOTIFICATION FUNCTIONS ---
export const getNotifications = async (): Promise<Notification[]> => {
    const q = query(collection(db, NOTIFICATIONS_COLLECTION), orderBy("createdAt", "desc"), limit(200));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ ...d.data(), id: d.id } as Notification));
};

export const getNotificationsByUser = async (userId: string): Promise<Notification[]> => {
    const q = query(collection(db, NOTIFICATIONS_COLLECTION), where("userId", "==", userId));
    const snap = await getDocs(q);
    const notes = snap.docs.map(d => ({ ...d.data(), id: d.id } as Notification));
    return notes.sort((a,b) => b.createdAt - a.createdAt);
};

export const addNotification = async (notification: Omit<Notification, 'id' | 'createdAt' | 'status'>): Promise<Notification> => {
    const id = generateId();
    const newNotif: Notification = { ...notification, id, status: NotificationStatus.PENDING, createdAt: await fetchRealTime() };
    await setDoc(doc(db, NOTIFICATIONS_COLLECTION, id), newNotif);
    return newNotif;
};

export const updateNotificationStatus = async (id: string, status: NotificationStatus): Promise<void> => {
    await updateDoc(doc(db, NOTIFICATIONS_COLLECTION, id), { status });
};

export const deleteNotification = async (id: string): Promise<void> => {
    await deleteDoc(doc(db, NOTIFICATIONS_COLLECTION, id));
};

// --- SALARY FUNCTIONS ---
export const getSalaryRecords = async (month: string): Promise<SalaryRecord[]> => {
    const q = query(collection(db, SALARY_COLLECTION), where("month", "==", month));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ ...d.data(), id: d.id } as SalaryRecord));
};

export const saveSalaryRecord = async (record: SalaryRecord): Promise<void> => {
    const id = `${record.userId}_${record.month}`;
    await setDoc(doc(db, SALARY_COLLECTION, id), { ...record, id, updatedAt: Date.now() });
};

export const deleteSalaryRecordsForMonth = async (month: string): Promise<void> => {
    const q = query(collection(db, SALARY_COLLECTION), where("month", "==", month));
    const snap = await getDocs(q);
    const batch = writeBatch(db);
    snap.docs.forEach(d => batch.delete(d.ref));
    await batch.commit();
};

// --- CONFIG ---
export const getConfig = async (): Promise<AppConfig> => {
  const docRef = doc(db, CONFIG_COLLECTION, CONFIG_DOC_ID);
  const d = await getDoc(docRef);
  if (d.exists()) {
    const data = d.data() as AppConfig;
    return { ...INITIAL_CONFIG, ...data };
  } else {
    await setDoc(docRef, INITIAL_CONFIG);
    return INITIAL_CONFIG;
  }
};

export const updateConfig = async (config: AppConfig): Promise<void> => {
    await setDoc(doc(db, CONFIG_COLLECTION, CONFIG_DOC_ID), config);
};

// --- BACKUP & RESTORE ---
export interface BackupData {
    metadata: { date: string; version: string; appName: string; };
    users: User[];
    logs: AttendanceLog[];
    leaves: LeaveRecord[];
    config: AppConfig;
    notifications: Notification[];
}

export const createFullBackup = async (): Promise<BackupData> => {
    const [users, leaves, config, notifications] = await Promise.all([
        getUsers(), getLeaves(), getConfig(), getNotifications()
    ]);
    
    // SAFETY FIX: Firestore has a max limit of 10,000 documents per query for `limit()`.
    // We must use pagination (batches) to fetch more than 10k logs.
    
    let allLogs: AttendanceLog[] = [];
    let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
    let fetchMore = true;
    const BATCH_SIZE = 5000; // Safe batch size well under 10k limit
    const MAX_LIMIT = 100000; // Application safety cap to prevent browser memory crash

    while(fetchMore && allLogs.length < MAX_LIMIT) {
         let q: Query<DocumentData>;
         if (lastDoc) {
             q = query(collection(db, LOGS_COLLECTION), orderBy("timestamp", "desc"), startAfter(lastDoc), limit(BATCH_SIZE));
         } else {
             q = query(collection(db, LOGS_COLLECTION), orderBy("timestamp", "desc"), limit(BATCH_SIZE));
         }

         const snap = await getDocs(q);
         const batch = snap.docs.map(d => ({...d.data(), id: d.id} as AttendanceLog));
         
         if (batch.length === 0) {
             fetchMore = false;
         } else {
             allLogs = [...allLogs, ...batch];
             lastDoc = snap.docs[snap.docs.length - 1];
             if (batch.length < BATCH_SIZE) fetchMore = false;
         }
    }
    
    return {
        metadata: { date: new Date().toISOString(), version: '2.1', appName: 'Technosis_Personel_Takip' },
        users, logs: allLogs, leaves, config, notifications
    };
};

export const restoreFromBackup = async (data: BackupData, onProgress?: (percent: number) => void): Promise<{success: boolean, message: string}> => {
    if (!data || !data.users || !data.config) return { success: false, message: "Geçersiz yedek dosyası." };
    try {
        if (onProgress) onProgress(5);
        await updateConfig(data.config);
        
        const totalItems = (data.users?.length || 0) + (data.logs?.length || 0) + (data.leaves?.length || 0) + (data.notifications?.length || 0);
        let processedItems = 0;

        const writeInBatches = async (collectionName: string, items: any[]) => {
            if (!items || items.length === 0) return;
            const CHUNK_SIZE = 450; // Firestore batch limit is 500, keeping safe buffer
            for (let i = 0; i < items.length; i += CHUNK_SIZE) {
                const chunk = items.slice(i, i + CHUNK_SIZE);
                const batch = writeBatch(db);
                chunk.forEach(item => { 
                    if(!item.id) item.id = generateId(); 
                    batch.set(doc(db, collectionName, item.id), item, { merge: true }); 
                });
                await batch.commit();
                
                // Update Progress
                processedItems += chunk.length;
                if(onProgress && totalItems > 0) {
                     const p = 5 + Math.floor((processedItems / totalItems) * 95);
                     onProgress(Math.min(p, 100));
                }
                
                // Yield to main thread to prevent UI freeze
                await new Promise(resolve => setTimeout(resolve, 50));
            }
        };

        await writeInBatches(USERS_COLLECTION, data.users);
        if (data.logs?.length) await writeInBatches(LOGS_COLLECTION, data.logs);
        if (data.leaves?.length) await writeInBatches(LEAVES_COLLECTION, data.leaves);
        if (data.notifications?.length) await writeInBatches(NOTIFICATIONS_COLLECTION, data.notifications);
        
        return { success: true, message: "Yedek başarıyla geri yüklendi." };
    } catch (error: any) { 
        return { success: false, message: "Geri yükleme hatası: " + error.message }; 
    }
};

// --- CLOUD BACKUP (CHUNKING IMPLEMENTATION) ---
// Firestore has a 1MB limit per document. We must chunk large backups.

export const getCloudBackups = async (): Promise<CloudBackup[]> => {
    // Only fetch metadata doc, not subcollections
    const q = query(collection(db, BACKUPS_COLLECTION), orderBy("timestamp", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ ...d.data(), id: d.id } as CloudBackup));
};

export const saveCloudBackup = async (type: 'AUTO' | 'MANUAL', onProgress?: (msg: string) => void): Promise<CloudBackup> => {
    if (onProgress) onProgress("Veriler toplanıyor...");
    const backupData = await createFullBackup();
    
    if (onProgress) onProgress("Veri paketleniyor...");
    const jsonString = JSON.stringify(backupData);
    const sizeInBytes = new Blob([jsonString]).size;
    const sizeStr = sizeInBytes > 1024 * 1024 ? `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB` : `${(sizeInBytes / 1024).toFixed(0)} KB`;
    
    const today = new Date().toISOString().split('T')[0];
    const docId = type === 'AUTO' ? `backup_auto_${today}` : `backup_manual_${Date.now()}`;
    
    // Chunking Logic 
    // OLD RISK: 800KB chars (could be >1MB bytes with UTF-8 overhead)
    // FIX: 400KB chars. Even if every char is 2 bytes (Turkish/Special chars), it's 800KB max. 
    // This is safely under the 1MB Firestore limit including overhead.
    const CHUNK_SIZE_CHARS = 400 * 1024; 
    const totalChunks = Math.ceil(jsonString.length / CHUNK_SIZE_CHARS);
    
    if (onProgress) onProgress(`Yedek ${totalChunks} parçaya bölünüyor...`);

    // Save Main Metadata Document
    const cloudBackup: CloudBackup = { 
        id: docId, 
        date: new Date().toISOString(), 
        timestamp: Date.now(), 
        data: "", // Main doc data empty for new version
        size: sizeStr, 
        creator: type,
        totalChunks: totalChunks,
        version: '2.1',
        status: 'PARTIAL' // Mark as partial until all chunks written
    };
    
    await setDoc(doc(db, BACKUPS_COLLECTION, docId), cloudBackup);

    // Save Chunks in Subcollection
    // Batch writes for chunks (Firestore limit 500 ops per batch)
    // Using 100 for batch size for chunks is safe and responsive
    
    const chunksArray = [];
    for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE_CHARS;
        const end = start + CHUNK_SIZE_CHARS;
        const chunkData = jsonString.substring(start, end);
        chunksArray.push({ index: i, data: chunkData });
    }

    const BATCH_SIZE = 100;
    for (let i = 0; i < chunksArray.length; i += BATCH_SIZE) {
         if (onProgress) onProgress(`Parçalar buluta yükleniyor (${i + 1}/${totalChunks})...`);
         const batch = writeBatch(db);
         const currentBatchChunks = chunksArray.slice(i, i + BATCH_SIZE);
         
         currentBatchChunks.forEach(chunk => {
             const chunkRef = doc(collection(db, BACKUPS_COLLECTION, docId, 'chunks'), `chunk_${chunk.index}`);
             batch.set(chunkRef, chunk);
         });
         
         await batch.commit();
         // Yield to prevent UI freeze
         await new Promise(r => setTimeout(r, 50));
    }

    // Mark complete
    await updateDoc(doc(db, BACKUPS_COLLECTION, docId), { status: 'COMPLETE' });
    
    if (onProgress) onProgress("Yedekleme tamamlandı.");
    return { ...cloudBackup, status: 'COMPLETE' };
};

export const checkAndTriggerAutoBackup = async () => {
    try {
        const config = await getConfig();
        const now = new Date();
        const todayStr = now.toISOString().split('T')[0];
        if (now.getHours() >= 18 && config.lastAutoBackupDate !== todayStr) {
            console.log("Starting Auto Backup...");
            await saveCloudBackup('AUTO');
            await updateConfig({ ...config, lastAutoBackupDate: todayStr });
            console.log("Auto Backup Complete.");
        }
    } catch (e) { console.error("Auto Backup Failed:", e); }
};

export const restoreFromCloud = async (backupId: string, onProgress?: (msg: string) => void): Promise<{success: boolean, message: string}> => {
    try {
        if(onProgress) onProgress("Yedek verisi indiriliyor...");
        const docRef = doc(db, BACKUPS_COLLECTION, backupId);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) return { success: false, message: "Yedek bulunamadı." };
        
        let fullJson = "";
        const mainData = docSnap.data() as CloudBackup;

        // STRATEGY 1: Old Format (Data in main doc)
        if (mainData.data && mainData.data.length > 0) {
            fullJson = mainData.data;
        } 
        // STRATEGY 2: New Chunked Format
        else {
            const chunksQuery = query(collection(db, BACKUPS_COLLECTION, backupId, 'chunks'), orderBy('index'));
            const chunksSnap = await getDocs(chunksQuery);
            
            if (chunksSnap.empty) {
                return { success: false, message: "Yedek verisi bozuk veya eksik (parçalar bulunamadı)." };
            }
            
            if(onProgress) onProgress(`Veri birleştiriliyor (${chunksSnap.size} parça)...`);
            
            // Reassemble
            fullJson = chunksSnap.docs.map(d => d.data().data).join('');
        }

        if(onProgress) onProgress("Veri işleniyor...");
        let parsedData: BackupData;
        try {
            parsedData = JSON.parse(fullJson) as BackupData;
        } catch (e) {
            return { success: false, message: "Yedek dosyası bozuk (JSON parse hatası)." };
        }

        // Use standard restore function with percent progress
        return await restoreFromBackup(parsedData, (percent) => {
            if(onProgress) onProgress(`Veritabanına yazılıyor... %${percent}`);
        });

    } catch (e: any) { return { success: false, message: "Bulut yedeği okunamadı: " + e.message }; }
};

export const deleteCloudBackup = async (backupId: string): Promise<void> => {
    // 1. Delete all chunks in subcollection
    const chunksQuery = query(collection(db, BACKUPS_COLLECTION, backupId, 'chunks'));
    const chunksSnap = await getDocs(chunksQuery);
    
    // FIX: Batch delete must also be looped to avoid 500 ops limit
    const chunkDocs = chunksSnap.docs;
    const CHUNK_DELETE_BATCH_SIZE = 450;

    for (let i = 0; i < chunkDocs.length; i += CHUNK_DELETE_BATCH_SIZE) {
        const batch = writeBatch(db);
        const batchDocs = chunkDocs.slice(i, i + CHUNK_DELETE_BATCH_SIZE);
        batchDocs.forEach(d => batch.delete(d.ref));
        await batch.commit();
    }
    
    // 2. Delete main document
    // Can be done as a separate op
    await deleteDoc(doc(db, BACKUPS_COLLECTION, backupId));
};

export const fixDatabaseConsistency = async (): Promise<{totalFixed: number, details: string}> => {
    // Simple consistency check example
    const logs = await getLogs();
    const users = await getUsers();
    let fixed = 0;
    // Example: Ensure logs have valid userId
    // Real implementation would be more complex
    return { totalFixed: fixed, details: "Tutarlılık kontrolü tamamlandı." };
};
